<script setup lang="ts">
import { ref } from "vue";
import { v4 as uuidv4 } from "uuid";
import {
	Condition,
	createDraftCondition,
} from "@/modules/rule_engine/ruleEngineClasses";
import ConditionRow from "@/modules/rule_engine/components/conditions/ConditionRow.vue";

const conditions = defineModel<Condition[]>({ required: true });

/** Accordion: opening a condition closes the one already open. */
const expandedUuid = ref<string | null>(null);

const isExpanded = (uuid: string) => expandedUuid.value === uuid;

const toggle = (uuid: string) => {
	expandedUuid.value = expandedUuid.value === uuid ? null : uuid;
};

/**
 * A new condition starts empty — no dimension, no type — so the row opens on
 * the dimension picker rather than on a guess. Because nothing is derived from
 * the dimension list, adding one never depends on that list having loaded.
 */
const addCondition = () => {
	const condition = createDraftCondition(uuidv4());

	conditions.value.push(condition);
	expandedUuid.value = condition.uuid;
};

const removeCondition = (uuid: string) => {
	const index = conditions.value.findIndex(
		(condition) => condition.uuid === uuid
	);
	if (index !== -1) conditions.value.splice(index, 1);

	if (expandedUuid.value === uuid) expandedUuid.value = null;
};
</script>

<template>
	<div>
		<v-card class="pa-4" variant="elevated">
			<div v-if="conditions.length === 0" class="empty">
				<v-icon size="16" color="warning" class="mr-2">
					mdi-alert-circle-outline
				</v-icon>
				<i
					>Add at least one condition — this rule cannot be saved without
					one.</i
				>
			</div>

			<condition-row
				v-for="(condition, index) in conditions"
				:key="condition.uuid"
				v-model="conditions[index]"
				:index="index"
				:expanded="isExpanded(condition.uuid)"
				@toggle="toggle(condition.uuid)"
				@remove="removeCondition(condition.uuid)"
			/>

			<v-btn
				class="mt-3"
				variant="text"
				color="#1abc9c"
				prepend-icon="mdi-plus"
				@click="addCondition"
			>
				Add condition
			</v-btn>
		</v-card>
	</div>
</template>

<style scoped lang="scss">
.empty {
	display: flex;
	align-items: center;
	opacity: 0.85;
}
</style>
